/**
 * Réglages › Général: what the org IS — where it is, and what clock it runs on.
 *
 * Both sections seed `useState` from `activeOrg` on mount, so the component
 * below remounts them by `key` when the active org changes. Without it a
 * switch would leave the previous org's address sitting in the form.
 */
import type { ExitTrackingMode, WalkInIdPolicy } from "@gately/validators";
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";

import { Button } from "@gately/ui/button";
import { Input } from "@gately/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@gately/ui/select";
import { SettingRow, SettingsSection } from "@gately/ui/settings";
import { Switch } from "@gately/ui/switch";
import { toast } from "@gately/ui/toast";
import {
  exitTrackingModes,
  hhmmToMinutes,
  isAllowedMapsUrl,
  minutesToHhmm,
  zoneLabel,
} from "@gately/validators";

import { orpc } from "~/lib/orpc";
import { useMe } from "~/lib/use-me";

// The exact set `timeZoneSchema` accepts (packages/validators/src/pass-status.ts)
// -- offering a zone the validator would then reject is worse than no picker.
// Resolved once at module load, not per render.
const SUPPORTED_TIME_ZONES = Intl.supportedValuesOf("timeZone");

export const Route = createFileRoute("/_dashboard/reglages/general")({
  loader: ({ context }) =>
    context.queryClient.ensureQueryData(orpc.me.get.queryOptions()),
  component: GeneralTab,
});

function GeneralTab() {
  const queryClient = useQueryClient();
  const me = useMe();

  return (
    <div className="space-y-8">
      <LocationSection
        key={`location-${me.activeOrg?.id ?? "no-org"}`}
        activeOrg={me.activeOrg}
        queryClient={queryClient}
      />
      <TimezoneSection
        key={`timezone-${me.activeOrg?.id ?? "no-org"}`}
        activeOrg={me.activeOrg}
        queryClient={queryClient}
      />
      <DayEndSection
        key={`day-end-${me.activeOrg?.id ?? "no-org"}`}
        activeOrg={me.activeOrg}
        queryClient={queryClient}
      />
      <ExitTrackingSection
        key={`exit-tracking-${me.activeOrg?.id ?? "no-org"}`}
        activeOrg={me.activeOrg}
        queryClient={queryClient}
      />
      {/* Office only: the borne is an office feature, and the API refuses
          `required` for a résidence. */}
      {me.activeOrg?.type === "office" ? (
        <WalkInIdPolicySection
          key={`walk-in-id-${me.activeOrg.id}`}
          activeOrg={me.activeOrg}
          queryClient={queryClient}
        />
      ) : null}
    </div>
  );
}

type ActiveOrgLocation = {
  address: string | null;
  mapsUrl: string | null;
} | null;

function LocationSection({
  activeOrg,
  queryClient,
}: {
  activeOrg: ActiveOrgLocation;
  queryClient: ReturnType<typeof useQueryClient>;
}) {
  // Seeded on mount; the parent's `key` remounts this when the org changes.
  const [address, setAddress] = useState(activeOrg?.address ?? "");
  const [mapsUrl, setMapsUrl] = useState(activeOrg?.mapsUrl ?? "");

  // Same allowlist the server enforces (@gately/validators), run here only so
  // the syndic learns about a bad link before submitting — never instead of.
  const mapsUrlInvalid =
    mapsUrl.trim() !== "" && !isAllowedMapsUrl(mapsUrl.trim());

  const saveLocation = useMutation(
    orpc.organization.setLocation.mutationOptions({
      onSuccess: () => {
        toast.success("Localisation enregistrée.");
        void queryClient.invalidateQueries({ queryKey: orpc.me.get.key() });
      },
      onError: (err) => toast.error(err.message),
    }),
  );

  return (
    <SettingsSection
      title="Localisation"
      hint="Indiquée à chaque visiteur, dans l'invitation et sur le pass."
    >
      <SettingRow label="Adresse" htmlFor="org-address">
        <Input
          id="org-address"
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          placeholder="12 Rue Ibn Battouta, Casablanca"
          maxLength={300}
        />
      </SettingRow>

      <SettingRow
        label="Lien Google Maps (optionnel)"
        htmlFor="org-maps-url"
        // The one instruction that decides whether this feature works: an
        // address gets a driver to the street, only a pin gets them to the
        // gate.
        hint="Placez le repère sur l'entrée, pas sur le bâtiment."
        error={
          mapsUrlInvalid
            ? "Lien invalide. Collez un lien Google Maps (google.com/maps, maps.app.goo.gl ou goo.gl/maps)."
            : null
        }
      >
        <Input
          id="org-maps-url"
          value={mapsUrl}
          onChange={(e) => setMapsUrl(e.target.value)}
          placeholder="https://maps.app.goo.gl/…"
          aria-invalid={mapsUrlInvalid}
        />
      </SettingRow>

      <Button
        onClick={() =>
          saveLocation.mutate({
            address: address.trim() || null,
            mapsUrl: mapsUrl.trim() || null,
          })
        }
        disabled={saveLocation.isPending || mapsUrlInvalid}
      >
        Enregistrer la localisation
      </Button>
    </SettingsSection>
  );
}

type ActiveOrgTimezone = {
  timezone: string;
} | null;

function TimezoneSection({
  activeOrg,
  queryClient,
}: {
  activeOrg: ActiveOrgTimezone;
  queryClient: ReturnType<typeof useQueryClient>;
}) {
  // Seeded on mount; the parent's `key` remounts this when the org changes.
  const [timezone, setTimezone] = useState(
    activeOrg?.timezone ?? "Africa/Casablanca",
  );

  const saveTimezone = useMutation(
    orpc.organization.setTimezone.mutationOptions({
      onSuccess: () => {
        toast.success("Fuseau horaire enregistré.");
        void queryClient.invalidateQueries({ queryKey: orpc.me.get.key() });
      },
      onError: (err) => toast.error(err.message),
    }),
  );

  return (
    <SettingsSection
      title="Fuseau horaire"
      hint="L'horloge du portail : chaque heure de visite est écrite, appliquée et affichée dans ce fuseau."
    >
      <SettingRow
        label="Fuseau horaire"
        htmlFor="org-timezone"
        // Doc-comment on organization.setTimezone spells out why: existing
        // one-time/date-range passes are instants and don't move, but a
        // recurring pass's daily window is a wall-clock rule, so it does.
        hint="Les accès récurrents existants seront réinterprétés dans le nouveau fuseau."
      >
        <Select value={timezone} onValueChange={setTimezone}>
          <SelectTrigger id="org-timezone" className="w-full">
            <SelectValue placeholder="Choisir un fuseau horaire" />
          </SelectTrigger>
          <SelectContent>
            {SUPPORTED_TIME_ZONES.map((tz) => (
              <SelectItem key={tz} value={tz}>
                {zoneLabel(tz)} ({tz})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </SettingRow>

      <Button
        onClick={() => saveTimezone.mutate({ timezone })}
        disabled={
          saveTimezone.isPending || timezone === (activeOrg?.timezone ?? "")
        }
      >
        Enregistrer le fuseau horaire
      </Button>
    </SettingsSection>
  );
}

type ActiveOrgDayEnd = {
  dayEndMinutes: number;
} | null;

function DayEndSection({
  activeOrg,
  queryClient,
}: {
  activeOrg: ActiveOrgDayEnd;
  queryClient: ReturnType<typeof useQueryClient>;
}) {
  const current = activeOrg?.dayEndMinutes ?? 1439;
  // Seeded on mount; the parent's `key` remounts this when the org changes.
  const [hhmm, setHhmm] = useState(minutesToHhmm(current));
  const minutes = hhmmToMinutes(hhmm);

  const save = useMutation(
    orpc.organization.setDayEnd.mutationOptions({
      onSuccess: () => {
        toast.success("Fin de journée enregistrée.");
        void queryClient.invalidateQueries({ queryKey: orpc.me.get.key() });
      },
      onError: (err) => toast.error(err.message),
    }),
  );

  return (
    <SettingsSection
      title="Fin de journée"
      hint="Heure à laquelle une invitation d'un jour sans heure de fin cesse d'être valable."
    >
      <SettingRow
        label="Heure de fin"
        htmlFor="org-day-end"
        hint="S'applique aux nouvelles invitations d'un jour créées sans heure de fin — formulaire, texte collé, e-mail entrant. Les invitations existantes ne changent pas."
      >
        <Input
          id="org-day-end"
          type="time"
          value={hhmm}
          onChange={(e) => setHhmm(e.target.value)}
          className="w-40"
        />
      </SettingRow>

      <Button
        onClick={() => {
          if (minutes !== null) save.mutate({ dayEndMinutes: minutes });
        }}
        disabled={save.isPending || minutes === null || minutes === current}
      >
        Enregistrer la fin de journée
      </Button>
    </SettingsSection>
  );
}

const EXIT_TRACKING_LABELS: Record<ExitTrackingMode, string> = {
  off: "Non suivie",
  manual: "Suivie",
  badge: "Suivie avec badge",
};

const EXIT_TRACKING_HINTS: Record<ExitTrackingMode, string> = {
  off: "Seules les entrées sont enregistrées.",
  manual: "Le gardien enregistre la sortie en un geste.",
  badge: "Un code de badge peut être saisi à l'entrée.",
};

type ActiveOrgExitTracking = {
  // `organization.exitTracking` is a plain `text` column (not narrowed at
  // the schema level), so — same idiom as `activeOrg?.type` in
  // `~/lib/use-terms` — it's cast to the union at the read site.
  exitTracking: string;
} | null;

function ExitTrackingSection({
  activeOrg,
  queryClient,
}: {
  activeOrg: ActiveOrgExitTracking;
  queryClient: ReturnType<typeof useQueryClient>;
}) {
  const current = (activeOrg?.exitTracking as ExitTrackingMode) ?? "off";
  // Seeded on mount; the parent's `key` remounts this when the org changes.
  const [exitTracking, setExitTracking] = useState<ExitTrackingMode>(current);

  const save = useMutation(
    orpc.organization.setExitTracking.mutationOptions({
      onSuccess: (res) => {
        if (res.exitTracking === "off" && res.closedOnDisable > 0) {
          toast.success(
            `Suivi désactivé. ${res.closedOnDisable} visite(s) encore ouverte(s) ont été clôturées.`,
          );
        } else {
          toast.success("Sortie des visiteurs enregistrée.");
        }
        void queryClient.invalidateQueries({ queryKey: orpc.me.get.key() });
      },
      onError: (err) => toast.error(err.message),
    }),
  );

  return (
    <SettingsSection
      title="Sortie des visiteurs"
      hint="Choisissez si et comment la sortie d'un visiteur est enregistrée."
    >
      <SettingRow
        label="Sortie des visiteurs"
        htmlFor="org-exit-tracking"
        hint={EXIT_TRACKING_HINTS[exitTracking]}
      >
        <Select
          value={exitTracking}
          onValueChange={(v) => setExitTracking(v as ExitTrackingMode)}
        >
          <SelectTrigger id="org-exit-tracking" className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {exitTrackingModes.map((mode) => (
              <SelectItem key={mode} value={mode}>
                {EXIT_TRACKING_LABELS[mode]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </SettingRow>

      <Button
        onClick={() => save.mutate({ exitTracking })}
        disabled={save.isPending || exitTracking === current}
      >
        Enregistrer
      </Button>
    </SettingsSection>
  );
}

const WALK_IN_ID_HINTS: Record<WalkInIdPolicy, string> = {
  off: "Facultative, et seulement pour une livraison ou des travaux.",
  required:
    "Obligatoire pour toute arrivée imprévue, quel que soit le motif — à l'accueil comme sur la borne. Seuls les 3 derniers caractères sont conservés.",
};

type ActiveOrgWalkInIdPolicy = {
  // Plain `text` column — cast at the read site, as for `exitTracking`.
  walkInIdPolicy: string;
} | null;

function WalkInIdPolicySection({
  activeOrg,
  queryClient,
}: {
  activeOrg: ActiveOrgWalkInIdPolicy;
  queryClient: ReturnType<typeof useQueryClient>;
}) {
  const saved =
    (activeOrg?.walkInIdPolicy as WalkInIdPolicy | undefined) ?? "off";

  const save = useMutation(
    orpc.organization.setWalkInIdPolicy.mutationOptions({
      onSuccess: (res) => {
        toast.success(
          res.walkInIdPolicy === "required"
            ? "Pièce d'identité désormais obligatoire pour les arrivées imprévues."
            : "Pièce d'identité facultative pour les arrivées imprévues.",
        );
        void queryClient.invalidateQueries({ queryKey: orpc.me.get.key() });
      },
      onError: (err) => toast.error(err.message),
    }),
  );
  // While the write is in flight, show what was asked for, not the old value.
  const shown: WalkInIdPolicy = save.isPending
    ? save.variables.walkInIdPolicy
    : saved;

  return (
    <SettingsSection
      title="Arrivées imprévues"
      hint="Ce que l'accueil relève pour un visiteur sans invitation."
    >
      <SettingRow
        label="Pièce d'identité pour les arrivées imprévues"
        htmlFor="org-walk-in-id-policy"
        hint={WALK_IN_ID_HINTS[shown]}
      >
        <Switch
          id="org-walk-in-id-policy"
          checked={shown === "required"}
          disabled={save.isPending}
          onCheckedChange={(on) =>
            save.mutate({ walkInIdPolicy: on ? "required" : "off" })
          }
        />
      </SettingRow>
    </SettingsSection>
  );
}
